import { canonicalJson, sha256Hex } from '../../reportGeneration/reportContractHash';

/** Portable per-KPI uncertainty analysis authored before a revision or run exists. */
export const EO_KPI_UNCERTAINTY_DEFINITION_SCHEMA_VERSION = 1 as const;
export const EO_KPI_UNCERTAINTY_DEFINITION_CONTRACT_ID =
  'eo-kpi-uncertainty-analysis-definition-v1' as const;
export const EO_KPI_UNCERTAINTY_DIGEST_ALGORITHM = 'sha256-canonical-json-v1' as const;
/** Deliberate v1 envelope; larger factors require a reviewed contract revision. */
export const EO_KPI_UNCERTAINTY_MAX_COVERAGE_FACTOR = 10 as const;
export const EO_KPI_UNCERTAINTY_MAX_SOURCE_REFERENCES = 16 as const;
export const EO_KPI_UNCERTAINTY_MAX_ACCEPTED_ASSUMPTIONS = 32 as const;

export const EO_KPI_UNCERTAINTY_KPI_IDS = Object.freeze([
  'thermal_efficiency',
  'net_work',
  'pue',
  'wue',
  'erf',
] as const);

export type EoKpiUncertaintyKpiId = typeof EO_KPI_UNCERTAINTY_KPI_IDS[number];

export const EO_KPI_UNCERTAINTY_UNIT_BY_KPI = Object.freeze({
  thermal_efficiency: 'fraction',
  net_work: 'kW',
  pue: 'ratio',
  wue: 'L/kWh',
  erf: 'ratio',
} as const satisfies Readonly<Record<EoKpiUncertaintyKpiId, string>>);

export type EoKpiUncertaintyUnit =
  typeof EO_KPI_UNCERTAINTY_UNIT_BY_KPI[EoKpiUncertaintyKpiId];

export const EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES = Object.freeze([
  'numerical',
  'input_parameter',
  'model_form',
  'validation',
  'reference_data',
  'scenario_selection',
] as const);

export type EoKpiUncertaintyContributorCategory =
  typeof EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES[number];

export const EO_KPI_UNCERTAINTY_ESTIMATION_METHODS = Object.freeze([
  'numerical_convergence',
  'statistical_sample',
  'specification_tolerance',
  'sensitivity_propagation',
  'benchmark_discrepancy',
  'scenario_envelope',
  'documented_expert_judgment',
] as const);

export type EoKpiUncertaintyEstimationMethod =
  typeof EO_KPI_UNCERTAINTY_ESTIMATION_METHODS[number];

/** Exact semantic envelope prevents valid-looking category/method contradictions. */
export const EO_KPI_UNCERTAINTY_ESTIMATION_METHODS_BY_CATEGORY = Object.freeze({
  numerical: Object.freeze([
    'numerical_convergence',
    'documented_expert_judgment',
  ] as const),
  input_parameter: Object.freeze([
    'specification_tolerance',
    'statistical_sample',
    'sensitivity_propagation',
    'documented_expert_judgment',
  ] as const),
  model_form: Object.freeze([
    'benchmark_discrepancy',
    'statistical_sample',
    'documented_expert_judgment',
  ] as const),
  validation: Object.freeze([
    'benchmark_discrepancy',
    'statistical_sample',
    'documented_expert_judgment',
  ] as const),
  reference_data: Object.freeze([
    'specification_tolerance',
    'statistical_sample',
    'sensitivity_propagation',
    'documented_expert_judgment',
  ] as const),
  scenario_selection: Object.freeze([
    'scenario_envelope',
    'statistical_sample',
    'documented_expert_judgment',
  ] as const),
} as const satisfies Readonly<Record<
  EoKpiUncertaintyContributorCategory,
  ReadonlyArray<EoKpiUncertaintyEstimationMethod>
>>);

export interface EoKpiUncertaintyNamedPrincipalV1 {
  principalId: string;
  displayName: string;
}

export interface EoKpiUncertaintySourceReferenceV1 {
  referenceId: string;
  /** Immutable source revision, not a mutable head or URL. */
  revisionId: string;
  contentDigest: string;
}

export interface EoKpiUncertaintyContributorProvenanceV1 {
  schemaVersion: 1;
  estimationBasis: string;
  estimationMethod: EoKpiUncertaintyEstimationMethod;
  sourceReferences: ReadonlyArray<EoKpiUncertaintySourceReferenceV1>;
  /** Authored attribution only; this is not an authenticated approval. */
  declaredOwner: EoKpiUncertaintyNamedPrincipalV1;
  /** Authored attribution only; an independent server-owned V&V gate remains required. */
  declaredReviewer: EoKpiUncertaintyNamedPrincipalV1;
}

export interface EoKpiUncertaintyDefinitionContributorV1 {
  category: EoKpiUncertaintyContributorCategory;
  /** Standard uncertainty (k = 1), in the definition's canonical KPI unit. */
  standardUncertainty: number;
  provenance: EoKpiUncertaintyContributorProvenanceV1;
}

export type EoKpiUncertaintyCorrelationMatrixV1 =
  ReadonlyArray<ReadonlyArray<number>>;

interface EoKpiUncertaintyDefinitionBaseInputV1 {
  /** Portable model-content identifier; company/revision ownership is bound after publication. */
  definitionId: string;
  contributors: ReadonlyArray<EoKpiUncertaintyDefinitionContributorV1>;
  /** Rows and columns follow EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES. */
  correlationMatrix: EoKpiUncertaintyCorrelationMatrixV1;
  coverageFactor: number;
  acceptedAssumptions: ReadonlyArray<string>;
}

/** A discriminated input prevents pairing a KPI with another KPI's unit. */
export type EoKpiUncertaintyDefinitionBuildInputV1 =
  EoKpiUncertaintyDefinitionBaseInputV1 & {
    [KpiId in EoKpiUncertaintyKpiId]: {
      kpiId: KpiId;
      kpiUnit: typeof EO_KPI_UNCERTAINTY_UNIT_BY_KPI[KpiId];
    }
  }[EoKpiUncertaintyKpiId];

export interface EoKpiUncertaintyDefinitionV1 {
  schemaVersion: 1;
  contractId: typeof EO_KPI_UNCERTAINTY_DEFINITION_CONTRACT_ID;
  status: 'authored';
  definitionId: string;
  kpiId: EoKpiUncertaintyKpiId;
  kpiUnit: EoKpiUncertaintyUnit;
  correlationOrder: ReadonlyArray<EoKpiUncertaintyContributorCategory>;
  contributors: ReadonlyArray<EoKpiUncertaintyDefinitionContributorV1>;
  correlationMatrix: EoKpiUncertaintyCorrelationMatrixV1;
  coverageFactor: number;
  acceptedAssumptions: ReadonlyArray<string>;
  digestAlgorithm: typeof EO_KPI_UNCERTAINTY_DIGEST_ALGORITHM;
  definitionDigest: string;
}

export type EoKpiUncertaintyDefinitionErrorCode =
  | 'EO_UNCERTAINTY_INVALID_SHAPE'
  | 'EO_UNCERTAINTY_INVALID_IDENTITY'
  | 'EO_UNCERTAINTY_INVALID_KPI_UNIT'
  | 'EO_UNCERTAINTY_MISSING_CONTRIBUTOR'
  | 'EO_UNCERTAINTY_DUPLICATE_CONTRIBUTOR'
  | 'EO_UNCERTAINTY_INVALID_CONTRIBUTOR'
  | 'EO_UNCERTAINTY_INVALID_PROVENANCE'
  | 'EO_UNCERTAINTY_INVALID_REFERENCE'
  | 'EO_UNCERTAINTY_INVALID_CORRELATION_MATRIX'
  | 'EO_UNCERTAINTY_NON_PSD_CORRELATION_MATRIX'
  | 'EO_UNCERTAINTY_INVALID_COVERAGE_FACTOR'
  | 'EO_UNCERTAINTY_INVALID_ASSUMPTION'
  | 'EO_UNCERTAINTY_DIGEST_MISMATCH';

export class EoKpiUncertaintyDefinitionContractError extends TypeError {
  readonly code: EoKpiUncertaintyDefinitionErrorCode;
  readonly field: string;

  constructor(code: EoKpiUncertaintyDefinitionErrorCode, message: string, field: string) {
    super(`${code}: ${message} (${field})`);
    this.name = 'EoKpiUncertaintyDefinitionContractError';
    this.code = code;
    this.field = field;
  }
}

export type EoKpiUncertaintyDefinitionVerificationV1 =
  | { valid: true; definition: EoKpiUncertaintyDefinitionV1 }
  | {
    valid: false;
    error: Readonly<{
      code: EoKpiUncertaintyDefinitionErrorCode;
      field: string;
      message: string;
    }>;
  };

type UnknownRecord = Record<string, unknown>;
type DefinitionDigestBodyV1 = Omit<EoKpiUncertaintyDefinitionV1, 'definitionDigest'>;

const SHA256 = /^[a-f0-9]{64}$/u;
const BOUND_ID = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/u;
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f-\u009f]/u;
const PSD_TOLERANCE = 256 * Number.EPSILON
  * EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES.length;
const MAX_DISPLAY_NAME_BYTES = 128;
const MAX_ESTIMATION_BASIS_BYTES = 1_024;
const MAX_ASSUMPTION_BYTES = 512;

function fail(
  code: EoKpiUncertaintyDefinitionErrorCode,
  message: string,
  field: string,
): never {
  throw new EoKpiUncertaintyDefinitionContractError(code, message, field);
}

function record(value: unknown, field: string): UnknownRecord {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    fail('EO_UNCERTAINTY_INVALID_SHAPE', 'Expected an object.', field);
  }
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, expected: readonly string[], field: string): void {
  const keys = Object.keys(value).sort();
  const wanted = [...expected].sort();
  if (keys.length !== wanted.length || keys.some((key, index) => key !== wanted[index])) {
    fail('EO_UNCERTAINTY_INVALID_SHAPE', 'Object keys do not match the contract.', field);
  }
}

function containsUnpairedSurrogate(value: string): boolean {
  for (const character of value) {
    if (character.length === 1) {
      const code = character.charCodeAt(0);
      if (code >= 0xd800 && code <= 0xdfff) return true;
    }
  }
  return false;
}

function boundedText(
  value: unknown,
  field: string,
  maximumBytes: number,
  code: EoKpiUncertaintyDefinitionErrorCode,
): string {
  if (typeof value !== 'string'
      || value.length === 0
      || value !== value.trim()
      || value.normalize('NFC') !== value
      || CONTROL_CHARACTER.test(value)
      || containsUnpairedSurrogate(value)
      || new TextEncoder().encode(value).byteLength > maximumBytes) {
    fail(
      code,
      `Expected non-empty, trimmed NFC text of at most ${maximumBytes} UTF-8 bytes without control characters.`,
      field,
    );
  }
  return value;
}

function boundId(
  value: unknown,
  field: string,
  code: EoKpiUncertaintyDefinitionErrorCode,
): string {
  if (typeof value !== 'string' || !BOUND_ID.test(value)) {
    fail(code, 'Expected a 1-128 character path-safe identifier.', field);
  }
  return value;
}

function sha256(value: unknown, field: string): string {
  if (typeof value !== 'string' || !SHA256.test(value)) {
    fail('EO_UNCERTAINTY_INVALID_REFERENCE', 'Expected a lowercase SHA-256 digest.', field);
  }
  return value;
}

function finiteNonnegative(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || Object.is(value, -0)) {
    fail(
      'EO_UNCERTAINTY_INVALID_CONTRIBUTOR',
      'Expected a finite, nonnegative standard uncertainty without negative zero.',
      field,
    );
  }
  return value;
}

function parseKpi(
  kpiIdValue: unknown,
  kpiUnitValue: unknown,
  field: string,
): Readonly<{ kpiId: EoKpiUncertaintyKpiId; kpiUnit: EoKpiUncertaintyUnit }> {
  if (typeof kpiIdValue !== 'string'
      || !EO_KPI_UNCERTAINTY_KPI_IDS.includes(kpiIdValue as EoKpiUncertaintyKpiId)) {
    fail('EO_UNCERTAINTY_INVALID_IDENTITY', 'KPI id is not supported.', `${field}.kpiId`);
  }
  const kpiId = kpiIdValue as EoKpiUncertaintyKpiId;
  const kpiUnit = EO_KPI_UNCERTAINTY_UNIT_BY_KPI[kpiId];
  if (kpiUnitValue !== kpiUnit) {
    fail(
      'EO_UNCERTAINTY_INVALID_KPI_UNIT',
      `KPI ${kpiId} must use canonical unit ${kpiUnit}.`,
      `${field}.kpiUnit`,
    );
  }
  return Object.freeze({ kpiId, kpiUnit });
}

function parseNamedPrincipal(
  value: unknown,
  field: string,
): EoKpiUncertaintyNamedPrincipalV1 {
  const principal = record(value, field);
  exactKeys(principal, ['principalId', 'displayName'], field);
  return Object.freeze({
    principalId: boundId(
      principal.principalId,
      `${field}.principalId`,
      'EO_UNCERTAINTY_INVALID_PROVENANCE',
    ),
    displayName: boundedText(
      principal.displayName,
      `${field}.displayName`,
      MAX_DISPLAY_NAME_BYTES,
      'EO_UNCERTAINTY_INVALID_PROVENANCE',
    ),
  });
}

function parseSourceReferences(
  value: unknown,
  field: string,
): ReadonlyArray<EoKpiUncertaintySourceReferenceV1> {
  if (!Array.isArray(value)
      || value.length < 1
      || value.length > EO_KPI_UNCERTAINTY_MAX_SOURCE_REFERENCES) {
    fail(
      'EO_UNCERTAINTY_INVALID_REFERENCE',
      `Expected 1-${EO_KPI_UNCERTAINTY_MAX_SOURCE_REFERENCES} immutable source references.`,
      field,
    );
  }
  const referenceIds = new Set<string>();
  const contentDigests = new Set<string>();
  const parsed = value.map((entry, index) => {
    const entryField = `${field}[${index}]`;
    const reference = record(entry, entryField);
    exactKeys(reference, ['referenceId', 'revisionId', 'contentDigest'], entryField);
    const referenceId = boundId(
      reference.referenceId,
      `${entryField}.referenceId`,
      'EO_UNCERTAINTY_INVALID_REFERENCE',
    );
    const revisionId = boundId(
      reference.revisionId,
      `${entryField}.revisionId`,
      'EO_UNCERTAINTY_INVALID_REFERENCE',
    );
    const contentDigest = sha256(reference.contentDigest, `${entryField}.contentDigest`);
    if (referenceIds.has(referenceId) || contentDigests.has(contentDigest)) {
      fail(
        'EO_UNCERTAINTY_INVALID_REFERENCE',
        'Source reference identifiers and content digests must be unique.',
        entryField,
      );
    }
    referenceIds.add(referenceId);
    contentDigests.add(contentDigest);
    return Object.freeze({ referenceId, revisionId, contentDigest });
  });
  return Object.freeze(parsed);
}

function parseProvenance(
  value: unknown,
  field: string,
  category: EoKpiUncertaintyContributorCategory,
): EoKpiUncertaintyContributorProvenanceV1 {
  const provenance = record(value, field);
  exactKeys(provenance, [
    'schemaVersion',
    'estimationBasis',
    'estimationMethod',
    'sourceReferences',
    'declaredOwner',
    'declaredReviewer',
  ], field);
  if (provenance.schemaVersion !== 1) {
    fail('EO_UNCERTAINTY_INVALID_PROVENANCE', 'Provenance schema version must equal 1.', field);
  }
  if (typeof provenance.estimationMethod !== 'string'
      || !EO_KPI_UNCERTAINTY_ESTIMATION_METHODS.includes(
        provenance.estimationMethod as EoKpiUncertaintyEstimationMethod,
      )) {
    fail(
      'EO_UNCERTAINTY_INVALID_PROVENANCE',
      'Estimation method is not supported by this contract.',
      `${field}.estimationMethod`,
    );
  }
  const estimationMethod = provenance.estimationMethod as EoKpiUncertaintyEstimationMethod;
  const compatibleMethods: readonly EoKpiUncertaintyEstimationMethod[] =
    EO_KPI_UNCERTAINTY_ESTIMATION_METHODS_BY_CATEGORY[category];
  if (!compatibleMethods.includes(estimationMethod)) {
    fail(
      'EO_UNCERTAINTY_INVALID_PROVENANCE',
      `Estimation method ${estimationMethod} is not compatible with contributor ${category}.`,
      `${field}.estimationMethod`,
    );
  }
  const declaredOwner = parseNamedPrincipal(
    provenance.declaredOwner,
    `${field}.declaredOwner`,
  );
  const declaredReviewer = parseNamedPrincipal(
    provenance.declaredReviewer,
    `${field}.declaredReviewer`,
  );
  if (declaredOwner.principalId === declaredReviewer.principalId) {
    fail(
      'EO_UNCERTAINTY_INVALID_PROVENANCE',
      'Declared contributor owner and reviewer must be distinct principals.',
      `${field}.declaredReviewer.principalId`,
    );
  }
  return Object.freeze({
    schemaVersion: 1,
    estimationBasis: boundedText(
      provenance.estimationBasis,
      `${field}.estimationBasis`,
      MAX_ESTIMATION_BASIS_BYTES,
      'EO_UNCERTAINTY_INVALID_PROVENANCE',
    ),
    estimationMethod,
    sourceReferences: parseSourceReferences(
      provenance.sourceReferences,
      `${field}.sourceReferences`,
    ),
    declaredOwner,
    declaredReviewer,
  });
}

function parseContributors(
  value: unknown,
  field: string,
  requireCanonicalOrder: boolean,
): ReadonlyArray<EoKpiUncertaintyDefinitionContributorV1> {
  if (!Array.isArray(value)) {
    fail('EO_UNCERTAINTY_INVALID_SHAPE', 'Expected a contributor array.', field);
  }
  const byCategory = new Map<
    EoKpiUncertaintyContributorCategory,
    EoKpiUncertaintyDefinitionContributorV1
  >();
  for (let index = 0; index < value.length; index += 1) {
    const contributorField = `${field}[${index}]`;
    const contributor = record(value[index], contributorField);
    exactKeys(contributor, ['category', 'standardUncertainty', 'provenance'], contributorField);
    const category = contributor.category;
    if (typeof category !== 'string'
        || !EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES.includes(
          category as EoKpiUncertaintyContributorCategory,
        )) {
      fail(
        'EO_UNCERTAINTY_INVALID_CONTRIBUTOR',
        'Contributor category is not supported.',
        `${contributorField}.category`,
      );
    }
    const typedCategory = category as EoKpiUncertaintyContributorCategory;
    if (byCategory.has(typedCategory)) {
      fail(
        'EO_UNCERTAINTY_DUPLICATE_CONTRIBUTOR',
        `Contributor ${typedCategory} occurs more than once.`,
        `${contributorField}.category`,
      );
    }
    byCategory.set(typedCategory, Object.freeze({
      category: typedCategory,
      standardUncertainty: finiteNonnegative(
        contributor.standardUncertainty,
        `${contributorField}.standardUncertainty`,
      ),
      provenance: parseProvenance(
        contributor.provenance,
        `${contributorField}.provenance`,
        typedCategory,
      ),
    }));
  }

  for (const category of EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES) {
    if (!byCategory.has(category)) {
      fail(
        'EO_UNCERTAINTY_MISSING_CONTRIBUTOR',
        `Required contributor ${category} is missing.`,
        field,
      );
    }
  }
  if (byCategory.size !== EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES.length) {
    fail('EO_UNCERTAINTY_INVALID_CONTRIBUTOR', 'Contributor count is invalid.', field);
  }

  const canonical = EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES.map(category => {
    const contributor = byCategory.get(category);
    if (!contributor) {
      return fail('EO_UNCERTAINTY_MISSING_CONTRIBUTOR', 'Contributor is missing.', field);
    }
    return contributor;
  });
  if (requireCanonicalOrder && value.some((entry, index) => {
    const row = entry as UnknownRecord;
    return row.category !== EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES[index];
  })) {
    fail(
      'EO_UNCERTAINTY_INVALID_CONTRIBUTOR',
      'Persisted contributors are not in canonical correlation order.',
      field,
    );
  }
  return Object.freeze(canonical);
}

function assertPositiveSemidefinite(
  matrix: readonly (readonly number[])[],
  field: string,
): void {
  const size = matrix.length;
  const lower = Array.from({ length: size }, () => Array<number>(size).fill(0));
  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column <= row; column += 1) {
      let remainder = matrix[row]?.[column] ?? Number.NaN;
      for (let index = 0; index < column; index += 1) {
        remainder -= (lower[row]?.[index] ?? 0) * (lower[column]?.[index] ?? 0);
      }
      if (row === column) {
        if (remainder < -PSD_TOLERANCE) {
          fail(
            'EO_UNCERTAINTY_NON_PSD_CORRELATION_MATRIX',
            'Correlation matrix is not positive semidefinite.',
            field,
          );
        }
        if (lower[row]) {
          lower[row][column] = remainder > PSD_TOLERANCE ? Math.sqrt(remainder) : 0;
        }
      } else {
        const pivot = lower[column]?.[column] ?? 0;
        if (pivot > PSD_TOLERANCE) {
          if (lower[row]) lower[row][column] = remainder / pivot;
        } else if (Math.abs(remainder) > PSD_TOLERANCE) {
          fail(
            'EO_UNCERTAINTY_NON_PSD_CORRELATION_MATRIX',
            'Correlation matrix is not positive semidefinite.',
            field,
          );
        }
      }
    }
  }
}

function parseCorrelationMatrix(
  value: unknown,
  field: string,
): EoKpiUncertaintyCorrelationMatrixV1 {
  const size = EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES.length;
  if (!Array.isArray(value) || value.length !== size) {
    fail(
      'EO_UNCERTAINTY_INVALID_CORRELATION_MATRIX',
      `Expected an exact ${size}x${size} correlation matrix.`,
      field,
    );
  }
  const matrix = value.map((rawRow, rowIndex) => {
    if (!Array.isArray(rawRow) || rawRow.length !== size) {
      fail(
        'EO_UNCERTAINTY_INVALID_CORRELATION_MATRIX',
        `Expected an exact ${size}x${size} correlation matrix.`,
        `${field}[${rowIndex}]`,
      );
    }
    return Object.freeze(rawRow.map((entry, columnIndex) => {
      if (typeof entry !== 'number'
          || !Number.isFinite(entry)
          || entry < -1
          || entry > 1
          || Object.is(entry, -0)) {
        fail(
          'EO_UNCERTAINTY_INVALID_CORRELATION_MATRIX',
          'Correlation coefficients must be finite values in [-1, 1] without negative zero.',
          `${field}[${rowIndex}][${columnIndex}]`,
        );
      }
      return entry;
    }));
  });

  for (let row = 0; row < size; row += 1) {
    if (matrix[row]?.[row] !== 1) {
      fail(
        'EO_UNCERTAINTY_INVALID_CORRELATION_MATRIX',
        'Every correlation-matrix diagonal value must equal 1.',
        `${field}[${row}][${row}]`,
      );
    }
    for (let column = 0; column < row; column += 1) {
      if (matrix[row]?.[column] !== matrix[column]?.[row]) {
        fail(
          'EO_UNCERTAINTY_INVALID_CORRELATION_MATRIX',
          'Correlation matrix must be exactly symmetric.',
          `${field}[${row}][${column}]`,
        );
      }
    }
  }
  assertPositiveSemidefinite(matrix, field);
  return Object.freeze(matrix);
}

function coverageFactor(value: unknown, field: string): number {
  if (typeof value !== 'number'
      || !Number.isFinite(value)
      || value <= 0
      || value > EO_KPI_UNCERTAINTY_MAX_COVERAGE_FACTOR) {
    fail(
      'EO_UNCERTAINTY_INVALID_COVERAGE_FACTOR',
      `Coverage factor must be finite and in (0, ${EO_KPI_UNCERTAINTY_MAX_COVERAGE_FACTOR}].`,
      field,
    );
  }
  return value;
}

function parseAcceptedAssumptions(value: unknown, field: string): ReadonlyArray<string> {
  if (!Array.isArray(value)
      || value.length < 1
      || value.length > EO_KPI_UNCERTAINTY_MAX_ACCEPTED_ASSUMPTIONS) {
    fail(
      'EO_UNCERTAINTY_INVALID_ASSUMPTION',
      `Expected 1-${EO_KPI_UNCERTAINTY_MAX_ACCEPTED_ASSUMPTIONS} accepted assumptions.`,
      field,
    );
  }
  const seen = new Set<string>();
  const assumptions = value.map((entry, index) => {
    const assumption = boundedText(
      entry,
      `${field}[${index}]`,
      MAX_ASSUMPTION_BYTES,
      'EO_UNCERTAINTY_INVALID_ASSUMPTION',
    );
    if (seen.has(assumption)) {
      fail(
        'EO_UNCERTAINTY_INVALID_ASSUMPTION',
        'Accepted assumptions must be unique.',
        `${field}[${index}]`,
      );
    }
    seen.add(assumption);
    return assumption;
  });
  return Object.freeze(assumptions);
}

/** Authoritative digest over every definition field except the digest itself. */
export function eoKpiUncertaintyDefinitionDigestV1(body: DefinitionDigestBodyV1): string {
  return sha256Hex(canonicalJson(body));
}

function freezeDefinition(
  body: DefinitionDigestBodyV1,
  definitionDigest: string,
): EoKpiUncertaintyDefinitionV1 {
  return Object.freeze({ ...body, definitionDigest });
}

function definitionBody(
  definitionId: string,
  kpi: Readonly<{ kpiId: EoKpiUncertaintyKpiId; kpiUnit: EoKpiUncertaintyUnit }>,
  contributors: ReadonlyArray<EoKpiUncertaintyDefinitionContributorV1>,
  correlationMatrix: EoKpiUncertaintyCorrelationMatrixV1,
  parsedCoverageFactor: number,
  acceptedAssumptions: ReadonlyArray<string>,
): DefinitionDigestBodyV1 {
  return {
    schemaVersion: EO_KPI_UNCERTAINTY_DEFINITION_SCHEMA_VERSION,
    contractId: EO_KPI_UNCERTAINTY_DEFINITION_CONTRACT_ID,
    status: 'authored',
    definitionId,
    kpiId: kpi.kpiId,
    kpiUnit: kpi.kpiUnit,
    correlationOrder: Object.freeze([...EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES]),
    contributors,
    correlationMatrix,
    coverageFactor: parsedCoverageFactor,
    acceptedAssumptions,
    digestAlgorithm: EO_KPI_UNCERTAINTY_DIGEST_ALGORITHM,
  };
}

/** Build an immutable revision definition. No result value or computed total is accepted. */
export function buildEoKpiUncertaintyDefinitionV1(
  input: EoKpiUncertaintyDefinitionBuildInputV1,
): EoKpiUncertaintyDefinitionV1 {
  const source = record(input, 'definitionInput');
  exactKeys(source, [
    'definitionId',
    'kpiId',
    'kpiUnit',
    'contributors',
    'correlationMatrix',
    'coverageFactor',
    'acceptedAssumptions',
  ], 'definitionInput');
  const body = definitionBody(
    boundId(
      source.definitionId,
      'definitionInput.definitionId',
      'EO_UNCERTAINTY_INVALID_IDENTITY',
    ),
    parseKpi(source.kpiId, source.kpiUnit, 'definitionInput'),
    parseContributors(source.contributors, 'definitionInput.contributors', false),
    parseCorrelationMatrix(source.correlationMatrix, 'definitionInput.correlationMatrix'),
    coverageFactor(source.coverageFactor, 'definitionInput.coverageFactor'),
    parseAcceptedAssumptions(
      source.acceptedAssumptions,
      'definitionInput.acceptedAssumptions',
    ),
  );
  return freezeDefinition(body, eoKpiUncertaintyDefinitionDigestV1(body));
}

/** Fail-closed parser for revision persistence and server-side budget construction. */
export function parseEoKpiUncertaintyDefinitionV1(
  value: unknown,
): EoKpiUncertaintyDefinitionV1 {
  const definition = record(value, 'definition');
  exactKeys(definition, [
    'schemaVersion',
    'contractId',
    'status',
    'definitionId',
    'kpiId',
    'kpiUnit',
    'correlationOrder',
    'contributors',
    'correlationMatrix',
    'coverageFactor',
    'acceptedAssumptions',
    'digestAlgorithm',
    'definitionDigest',
  ], 'definition');
  if (definition.schemaVersion !== EO_KPI_UNCERTAINTY_DEFINITION_SCHEMA_VERSION
      || definition.contractId !== EO_KPI_UNCERTAINTY_DEFINITION_CONTRACT_ID
      || definition.status !== 'authored'
      || definition.digestAlgorithm !== EO_KPI_UNCERTAINTY_DIGEST_ALGORITHM) {
    fail('EO_UNCERTAINTY_INVALID_SHAPE', 'Definition constants do not match v1.', 'definition');
  }
  if (!Array.isArray(definition.correlationOrder)
      || definition.correlationOrder.length
        !== EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES.length
      || definition.correlationOrder.some(
        (entry, index) => entry !== EO_KPI_UNCERTAINTY_CONTRIBUTOR_CATEGORIES[index],
      )) {
    fail(
      'EO_UNCERTAINTY_INVALID_CORRELATION_MATRIX',
      'Correlation order must be the exact contributor order.',
      'definition.correlationOrder',
    );
  }
  const body = definitionBody(
    boundId(
      definition.definitionId,
      'definition.definitionId',
      'EO_UNCERTAINTY_INVALID_IDENTITY',
    ),
    parseKpi(definition.kpiId, definition.kpiUnit, 'definition'),
    parseContributors(definition.contributors, 'definition.contributors', true),
    parseCorrelationMatrix(definition.correlationMatrix, 'definition.correlationMatrix'),
    coverageFactor(definition.coverageFactor, 'definition.coverageFactor'),
    parseAcceptedAssumptions(
      definition.acceptedAssumptions,
      'definition.acceptedAssumptions',
    ),
  );
  if (typeof definition.definitionDigest !== 'string'
      || !SHA256.test(definition.definitionDigest)) {
    fail(
      'EO_UNCERTAINTY_DIGEST_MISMATCH',
      'Definition digest is not lowercase SHA-256.',
      'definition.definitionDigest',
    );
  }
  const expectedDigest = eoKpiUncertaintyDefinitionDigestV1(body);
  if (definition.definitionDigest !== expectedDigest) {
    fail(
      'EO_UNCERTAINTY_DIGEST_MISMATCH',
      'Definition digest does not match the canonical payload.',
      'definition.definitionDigest',
    );
  }
  return freezeDefinition(body, expectedDigest);
}

export function verifyEoKpiUncertaintyDefinitionV1(
  value: unknown,
): EoKpiUncertaintyDefinitionVerificationV1 {
  try {
    return { valid: true, definition: parseEoKpiUncertaintyDefinitionV1(value) };
  } catch (error) {
    if (error instanceof EoKpiUncertaintyDefinitionContractError) {
      return {
        valid: false,
        error: Object.freeze({ code: error.code, field: error.field, message: error.message }),
      };
    }
    return {
      valid: false,
      error: Object.freeze({
        code: 'EO_UNCERTAINTY_INVALID_SHAPE',
        field: 'definition',
        message: error instanceof Error ? error.message : 'Unknown uncertainty-definition error.',
      }),
    };
  }
}
